import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, DocumentDownload, Eye } from 'iconsax-react';
import { Modal } from 'antd';
import { DocumentInfo } from '../../../types/support';
import { formatFileSize, formatDocumentUrl, handleDownload } from './utils';

interface TicketAttachmentsProps {
  documents: DocumentInfo[];
}

const isImageFile = (name: string) =>
  /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(name ?? '');

const isPdfFile = (name: string) => /\.pdf(\?|$)/i.test(name ?? '');

export const TicketAttachments: React.FC<TicketAttachmentsProps> = ({
  documents,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(
    null
  );

  if (!documents || documents.length === 0) return null;

  const isImage = preview ? isImageFile(preview.name) : false;

  const handlePreview = (url: string, name: string) => {
    const formattedUrl = formatDocumentUrl(url);
    if (isPdfFile(name)) {
      const previewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        formattedUrl
      )}&embedded=true`;
      window.open(previewUrl, '_blank');
    } else {
      setPreview({ url: formattedUrl, name });
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-light-text-primary mb-3">
        {t('support.attachment_single', 'Attachment')}
      </h3>
      <div className="flex flex-col gap-3">
        {documents.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between rounded-2xl px-4 py-2 bg-white"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-12 h-12 rounded-xl border border-[#E5E7EB] bg-white flex items-center justify-center shrink-0">
                <Document size={24} className="text-gray-900" variant="Linear" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-light-text-primary text-sm truncate">
                  {doc.name || doc.blobName || 'Document'}
                </p>
                <p className="text-text-secondary text-xs mt-1">
                  {formatFileSize(doc.blobSize)} {t('support.completed', 'Completed')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              {doc.blobFileUrl && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      handleDownload(
                        doc.blobFileUrl!,
                        doc.name || doc.blobName,
                        t
                      )
                    }
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#ECFDF3] text-[#12B76A] hover:bg-[#D1FADF] transition-colors"
                    title={t('support.download', 'Download')}
                  >
                    <DocumentDownload size={18} variant="Linear" />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handlePreview(
                        doc.blobFileUrl!,
                        doc.name || doc.blobName || 'Attachment'
                      )
                    }
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EFF8FF] text-[#175CD3] hover:bg-[#D1E9FF] transition-colors"
                    title={t('support.view', 'View')}
                  >
                    <Eye size={18} variant="Linear" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 900 }}
        title={preview?.name}
        destroyOnHidden
        centered
      >
        {preview && (
          <div className="flex justify-center bg-gray-50 rounded-lg min-h-[60vh] overflow-auto">
            {isImage ? (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : (
              <iframe
                title={preview.name}
                src={preview.url}
                className="w-full min-h-[60vh] border-0 rounded-lg"
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TicketAttachments;
